import { Paper, BottomNavigation, BottomNavigationAction, Box, Badge, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';
import { PointOfSale, Receipt, Description, Cancel } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';

// Altura mínima para touch target
const TOUCH_TARGET_SIZE = 48;

const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modo, cajaEstado, toggleModo } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estado local para evitar stale closure
  const [modoActual, setModoActual] = useState(modo);
  const [dteDialogOpen, setDteDialogOpen] = useState(false);

  // Obtener módulos autorizados
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const authorizedModules = getAuthorizedModules();
  const hasPOS = authorizedModules.includes('POS');
  const hasFacturacion = authorizedModules.includes('FACTURACION');

  // Sincronizar estado local con el contexto
  useEffect(() => {
    setModoActual(modo);
  }, [modo]);

  // Solo mostrar en móvil
  if (!isMobile) return null;

  // Si no tiene POS autorizado, mostrar solo opciones de Facturación
  if (!hasPOS && hasFacturacion) {
    // Menú solo para Facturación (sin POS)
    const handleChange = (event, newValue) => {
      switch (newValue) {
        case 0:
          // Nuevo DTE - navegar al wizard de nuevo DTE
          navigate('/billing/nuevo-dte');
          break;
        case 1:
          // Anular DTE - navegar al wizard de anulación
          navigate('/billing/anulacion');
          break;
        default:
          break;
      }
    };

    const getSelectedIndex = () => {
      if (location.pathname.startsWith('/billing')) return 0;
      return 0;
    };

    return (
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          zIndex: 1200,
        }} 
        elevation={8}
      >
        <BottomNavigation 
          value={getSelectedIndex()} 
          onChange={handleChange}
          showLabels
          sx={{ 
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '8px 12px',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontSize: '0.75rem',
                fontWeight: 'bold',
              },
            },
          }}
        >
          <BottomNavigationAction 
            label="Nuevo DTE" 
            icon={<Description sx={{ fontSize: 28 }} />} 
            sx={{ 
              color: 'text.secondary',
              minHeight: TOUCH_TARGET_SIZE,
            }}
          />
          <BottomNavigationAction 
            label="Anular DTE" 
            icon={<Cancel sx={{ fontSize: 28 }} />} 
            sx={{ 
              color: 'text.secondary',
              minHeight: TOUCH_TARGET_SIZE,
            }}
          />
        </BottomNavigation>
      </Paper>
    );
  }

  // Menú completo (con POS)
  const handleChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        if (modoActual !== 'POS') {
          toggleModo();
        }
        navigate('/pos');
        break;
      case 1:
        if (modoActual !== 'FACTURACION') {
          toggleModo();
        }
        navigate('/billing');
        break;
      case 2:
        // DTE - abrir diálogo
        setDteDialogOpen(true);
        break;
      default:
        break;
    }
  };

  // Determinar qué icono mostrar según la ruta actual
  const getSelectedIndex = () => {
    if (location.pathname.startsWith('/pos')) return 0;
    if (location.pathname.startsWith('/billing')) return 1;
    return 0;
  };

  const isCajaAbierta = cajaEstado === 'abierta';

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0,
        zIndex: 1200,
      }} 
      elevation={8}
    >
      <BottomNavigation 
        value={getSelectedIndex()} 
        onChange={handleChange}
        showLabels
        sx={{ 
          height: 64,
          // Eliminar hover states para touch
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '8px 12px',
            transition: 'background-color 0.2s ease',
            // Touch feedback visual
            '&:active': {
              backgroundColor: 'action.selected',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          },
        }}
      >
        <BottomNavigationAction 
          label="POS" 
          icon={
            <Badge 
              color="success" 
              variant="dot" 
              invisible={!isCajaAbierta}
              sx={{
                '& .MuiBadge-badge': {
                  right: 3,
                  top: 3,
                },
              }}
            >
              <PointOfSale sx={{ fontSize: 28 }} />
            </Badge>
          } 
          sx={{ 
            color: (modoActual || modo) === 'POS' ? 'primary.main' : 'text.secondary',
            '&.Mui-selected': { 
              color: 'primary.main',
            },
            // Touch target grande
            minHeight: TOUCH_TARGET_SIZE,
          }}
        />
        <BottomNavigationAction 
          label="Facturación" 
          icon={<Receipt sx={{ fontSize: 28 }} />} 
          sx={{ 
            color: (modoActual || modo) === 'FACTURACION' ? 'primary.main' : 'text.secondary',
            '&.Mui-selected': { 
              color: 'primary.main',
            },
            minHeight: TOUCH_TARGET_SIZE,
          }}
        />
        <BottomNavigationAction 
          label="DTE" 
          icon={<Description sx={{ fontSize: 28 }} />} 
          sx={{ 
            color: 'text.secondary',
            minHeight: TOUCH_TARGET_SIZE,
          }}
        />
      </BottomNavigation>

      {/* Diálogo de DTE */}
      <Dialog open={dteDialogOpen} onClose={() => setDteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Documentos Tributarios Electrónicos (DTE)</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              label="Buscar DTE"
              variant="outlined"
              placeholder="Buscar por número, cliente o fecha"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              Lista de documentos tributarios electrónicos emitidos.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDteDialogOpen(false)}>Cerrar</Button>
          <Button variant="contained">Ver Todos</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MobileNav;
